import type { Tile } from '@casa/model';

/**
 * The screen grid: equal cells, `spacing` apart, `margin` from the edge. With `rows` the grid fills
 * `height` (the legacy grid); without, its cells are square and it is as tall as its tiles need.
 */
export interface GridGeometry {
    width: number;
    height: number;
    columns: number;
    rows?: number;
    margin: number;
    spacing: number;
}

export interface TileRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

export const GRID_SPACING = { margin: 4, spacing: 6 } as const;

function cellSize(grid: GridGeometry) : { width: number; height: number } {
    const width = (grid.width - 2 * grid.margin - (grid.columns - 1) * grid.spacing) / grid.columns;
    const height = grid.rows === undefined ? width : (grid.height - 2 * grid.margin - (grid.rows - 1) * grid.spacing) / grid.rows;

    return { width, height };
}

/**
 * Where a tile goes on the screen. Positions are 1-based as in the dashboard; a span that runs
 * past the grid is cut off at its edge, as MAUI's `Grid` does in the legacy app. A scrolling grid
 * has no bottom edge.
 */
export function tileRect(layout: Tile['layout'], grid: GridGeometry) : TileRect {
    const cell = cellSize(grid);
    const column = Math.max(0, layout.x - 1);
    const row = Math.max(0, layout.y - 1);
    const columnSpan = Math.max(1, Math.min(layout.width, grid.columns - column));
    const rowSpan = grid.rows === undefined ? layout.height : Math.max(1, Math.min(layout.height, grid.rows - row));

    return {
        left: grid.margin + column * (cell.width + grid.spacing),
        top: grid.margin + row * (cell.height + grid.spacing),
        width: columnSpan * cell.width + (columnSpan - 1) * grid.spacing,
        height: rowSpan * cell.height + (rowSpan - 1) * grid.spacing,
    };
}

/** How tall a scrolling grid is: down to its lowest tile, and the margin below it. */
export function scrollingHeight(layouts: readonly Tile['layout'][], grid: GridGeometry) : number {
    const bottom = Math.max(0, ...layouts.map((layout) => tileRect(layout, grid)).map((rect) => rect.top + rect.height));

    return bottom === 0 ? 0 : bottom + grid.margin;
}

/** From the start of one cell to the start of the next, across and down: what a drag of one cell is. */
export function cellPitch(grid: GridGeometry) : { across: number; down: number } {
    const cell = cellSize(grid);

    return { across: cell.width + grid.spacing, down: cell.height + grid.spacing };
}
